import ConnectingIndicator from '@components/ConnectingIndicator/ConnectingIndicator';
import { TaskTenantName, TaskText, TransferTag } from '@components/Task';
import { TaskTimeDisplay } from '@components/Task/TaskTimeDisplay/TaskTimeDisplay';
import { getTestIdProps } from '@lib/testUtils';
import { inboxLocators } from '@locators/inbox';
import { ITask } from '@modules/TasksModule/types';
import getTaskTimeout from '@modules/TasksModule/lib/getTaskTimeout';
import { useLiveServicesMapping } from '@modules/ServiceModule/hooks';
import { Divider, Icon } from '@servicetitan/design-system';
import classNames from 'classnames';
import { useMemo } from 'react';
import noop from '../../../../../lib/utils/noop';
import { useTaskRowVoiceProps } from '../hooks/useTaskRowVoiceProps';
import { LiveServicesDetails } from './LiveServicesDetails';
import { TaskVoiceActions } from './TaskVoiceActions';
import styles from './TaskVoiceCard.module.css';
import { getTaskVoiceCardAction } from './action-utils';

type Props = {
  task: ITask;
  active: boolean;
  onClick?: () => void;
  divider?: boolean;
};

export const TaskVoiceCard = ({
  task,
  active,
  onClick = noop,
  divider = true,
}: Props) => {
  const {
    displayName,
    showInternalTag,
    shouldShowTenantTag,
    isMissed,
    isHeld,
    isWrapUp,
    isInbound,
    isAnswering,
    isConnecting,
    isOffered,
    isIncomingTransfer,
    isTransferring,
    transferText,
    campaignName,
    workflowName,
    isSelfTransfer,
    handleClick,
    time,
  } = useTaskRowVoiceProps(task, active, onClick);
  const { tenantId, queueName } = task;
  const liveServicesMapping = useLiveServicesMapping(task.to, task.tenantId);
  const { timeoutAt, timeoutType } = getTaskTimeout(task);
  const action = useMemo(
    () =>
      getTaskVoiceCardAction({
        isInbound,
        isTransferring,
        isWrapUp,
        isHeld,
        isMissed,
        isConnecting,
        isOffered,
        isSelfTransfer,
      }),
    [
      isInbound,
      isTransferring,
      isWrapUp,
      isHeld,
      isMissed,
      isConnecting,
      isOffered,
      isSelfTransfer,
    ],
  );

  return (
    <>
      <div
        className={classNames(styles.taskContainer, {
          [styles.active]: active && !isHeld,
          [styles.held]: isHeld || isTransferring,
        })}
        role='button'
        tabIndex={0}
        aria-label='Task Card'
        data-task-displayid={task.attributes?.displayId}
        onClick={handleClick}
        {...getTestIdProps(inboxLocators.customerSearch.taskRow)}
        data-task-id={task.id}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {isIncomingTransfer && (
          <TransferTag
            className={classNames({
              [styles.activeTag]: active || isHeld || isTransferring,
            })}
          />
        )}
        <div className={styles.taskDetails}>
          <div
            className={classNames(styles.header, {
              [styles.missed]: isMissed,
            })}
          >
            <div className={classNames(styles.nameHeader)}>
              {isMissed && (
                <Icon
                  name='phone_missed'
                  className={styles.missedIcon}
                  {...getTestIdProps(inboxLocators.taskList.missedCallIcon)}
                />
              )}
              <TaskText
                isHeadline
                text={displayName}
                tooltip={displayName}
                className={classNames(styles.name, {
                  [styles.missed]: isMissed,
                })}
                data-testid={inboxLocators.taskList.displayName}
              />
            </div>

            {time && (
              <TaskTimeDisplay
                time={time}
                timeoutAt={timeoutAt}
                timeoutType={timeoutType}
                isOffered={isOffered}
                isMissed={isMissed}
              />
            )}
          </div>

          {shouldShowTenantTag && tenantId && (
            <TaskTenantName
              tenantNameOrId={tenantId}
              data-testid={inboxLocators.taskList.tenantTag}
            />
          )}
          {showInternalTag && <TaskText text='Internal' isEyebrow />}
          <LiveServicesDetails
            notes={liveServicesMapping?.notes}
            url={liveServicesMapping?.url}
          />
        </div>

        <div className={styles.taskInfo}>
          {workflowName && (
            <TaskText
              label='Workflow:'
              text={workflowName}
              tooltip={workflowName}
              data-testid={inboxLocators.taskList.workflowName}
            />
          )}
          {queueName && (
            <TaskText
              label='Queue:'
              text={queueName}
              tooltip={queueName}
              data-testid={inboxLocators.taskList.taskTag}
            />
          )}
          {campaignName && (
            <TaskText
              label='Campaign:'
              text={campaignName}
              tooltip={campaignName}
              data-testid={inboxLocators.taskList.campaignName}
            />
          )}
        </div>
        {isTransferring && <ConnectingIndicator text={transferText} />}
        <TaskVoiceActions
          taskId={task.id}
          action={action}
          isAnswering={isAnswering}
          isConnecting={isConnecting}
          isOffered={isOffered}
          type={task.type}
        />
      </div>
      {divider && <Divider spacing='0' data-testid='task-card-divider' />}
    </>
  );
};
